import strings from '~/constants/strings';
import {
  BusinessHours,
  CalendarAvailabilityDay,
  DayAvailability,
  HourAvailability,
  SpecialAvailability,
  TempHourAvailability,
} from '../types';
import { isHoliday } from 'japanese-holidays';
import dayjs, { Dayjs } from 'dayjs';

export const weekDayArray = [
  {
    id: '1',
    label: strings.general.date.day_of_week_1,
  },
  {
    id: '2',
    label: strings.general.date.day_of_week_2,
  },
  {
    id: '3',
    label: strings.general.date.day_of_week_3,
  },
  {
    id: '4',
    label: strings.general.date.day_of_week_4,
  },
  {
    id: '5',
    label: strings.general.date.day_of_week_5,
  },
  {
    id: '6',
    label: strings.general.date.day_of_week_6,
  },
  {
    id: '7',
    label: strings.general.date.day_of_week_0,
  },
];

export const getFrontFiller = (
  dateOfWeek: number,
  currentDayjs: Dayjs,
): CalendarAvailabilityDay[] => {
  const startOfMonth = dateOfWeek === 0 ? 7 : dateOfWeek;
  const res: CalendarAvailabilityDay[] = [];
  for (let i = startOfMonth - 1; i > 0; i--) {
    const dateDayjs = currentDayjs.startOf('month').clone().subtract(i, 'days');
    res.push({
      id: dateDayjs.format('YYYY-MM-DD'),
      isCurrentMonth: false,
      label: dateDayjs.format('D'),
      dayOfWeek: startOfMonth - 1 - i,
      isSaturday: false,
      isSunday: false,
      availabilityList: [],
      isOffDay: false,
      isSpecialDay: false,
    });
  }
  return res;
};

export const getBackFiller = (
  dateOfWeek: number,
  currentDayjs: Dayjs,
): CalendarAvailabilityDay[] => {
  const endOfMonth = dateOfWeek === 0 ? 7 : dateOfWeek;
  const res: CalendarAvailabilityDay[] = [];
  for (let i = 1; i < 8 - endOfMonth; i++) {
    const dateDayjs = currentDayjs.endOf('month').clone().add(i, 'days');
    res.push({
      id: dateDayjs.format('YYYY-MM-DD'),
      isCurrentMonth: false,
      label: dateDayjs.format('D'),
      dayOfWeek: endOfMonth + i - 1,
      isSaturday: false,
      isSunday: false,
      availabilityList: [],
      isOffDay: false,
      isSpecialDay: false,
    });
  }
  return res;
};
export const getAvailabilityText = (hours: HourAvailability) => {
  const start = dayjs()
    .hour(hours.from.hour || 0)
    .minute(hours.from.minute || 0)
    .format('HH:mm');
  const end = dayjs()
    .hour(hours.to.hour || 0)
    .minute(hours.to.minute || 0)
    .format('HH:mm');
  return `${start} ~ ${end}`;
};

export const isDateSpecialDay = (
  year: string,
  month: string,
  day: number,
  specialDays: SpecialAvailability[],
): {
  isSpecialDay: boolean;
  hours: HourAvailability[];
} => {
  const specialDay = specialDays.find(
    (s) =>
      (s.year === parseInt(year) || s.year === null) &&
      (s.month === parseInt(month) || s.month === null) &&
      (s.day === day || s.day === null),
  );
  if (specialDay) {
    return {
      isSpecialDay: true,
      hours: specialDay.hours,
    };
  }
  return {
    isSpecialDay: false,
    hours: [],
  };
};

export const getWeeklyAvailability = (
  dayOfWeek: number,
  weekly: DayAvailability[],
): HourAvailability[] => {
  const dayAvailability = weekly.find((w) => w.days.includes(dayOfWeek));
  if (dayAvailability) {
    return dayAvailability.hours;
  }
  return [];
};

const getHolidayAvailability = (
  year: string,
  month: string,
  day: number,
  businessHours?: BusinessHours,
): HourAvailability[] | null => {
  const holidayHours = businessHours?.holidays?.hours;
  if (!holidayHours) {
    return null;
  }
  // Use YYYY-M-D (not MM/DD): callers often pass unpadded month/day from
  // `.toString()` / numeric parts; with customParseFormat, YYYY-MM-D rejects "2026-5-16"
  // and Invalid Date can make isHoliday() truthy, wrongly applying holiday hours.
  // parseInt month so zero-padded strings (e.g. "06") match `M` under strict parse too.
  const parsed = dayjs(
    `${year}-${parseInt(month, 10)}-${day}`,
    'YYYY-M-D',
  );
  if (!parsed.isValid()) {
    return null;
  }
  const currentDate = parsed.toDate();
  return isHoliday(currentDate) ? holidayHours : null;
};

// dayOfWeek: 0-6 (0: Sunday, 1: Monday, ..., 6: Saturday)
export const getDateAvailability = (
  year: string,
  month: string,
  day: number,
  dayOfWeek: number,
  businessHours?: BusinessHours,
): {
  isSpecialDay: boolean;
  isOffDay: boolean;
  hours: HourAvailability[];
} => {
  if (!businessHours || !businessHours.weekly) {
    return {
      isSpecialDay: false,
      isOffDay: false,
      hours: [],
    };
  }
  const { weekly, specialDays } = businessHours;
  const { isSpecialDay, hours } = isDateSpecialDay(
    year,
    month,
    day,
    specialDays || [],
  );
  if (isSpecialDay) {
    return {
      isSpecialDay: true,
      isOffDay: hours.length === 0,
      hours,
    };
  }
  const holidayHours = getHolidayAvailability(
    year,
    month,
    day,
    businessHours,
  );
  if (holidayHours) {
    return {
      isSpecialDay: false,
      isOffDay: holidayHours.length === 0,
      hours: holidayHours,
    };
  }
  const hoursWeekly = getWeeklyAvailability(dayOfWeek, weekly);
  return {
    isSpecialDay: false,
    isOffDay: hoursWeekly.length === 0,
    hours: hoursWeekly,
  };
};

export const generateAvailableDateArray = (
  selectedDate: string,
  businessHours?: BusinessHours,
): CalendarAvailabilityDay[][] => {
  const currentDayjs = dayjs(selectedDate, 'YYYY-MM-DD');
  const currentYear = currentDayjs.format('YYYY');
  const currentMonth = currentDayjs.format('MM');
  const startOfMonth = currentDayjs.startOf('month').day();
  const endOfMonth = currentDayjs.endOf('month').day();
  const daysInMonth = currentDayjs.daysInMonth();
  const frontFiller = getFrontFiller(startOfMonth, currentDayjs);
  const backFiller = getBackFiller(endOfMonth, currentDayjs);
  const res: CalendarAvailabilityDay[][] = [];
  let temp: CalendarAvailabilityDay[] = [...frontFiller];
  for (let i = 1; i <= daysInMonth; i++) {
    if (temp.length === 7) {
      res.push(temp);
      temp = [];
    }
    const currentDate = dayjs(
      `${currentYear}-${parseInt(currentMonth, 10)}-${i}`,
      'YYYY-M-D',
    ).toDate();
    const holidayName = isHoliday(currentDate) || undefined;
    const dayOfWeek = temp.length === 6 ? 0 : temp.length + 1;
    const dayStart = currentDayjs.clone().date(i).startOf('date');
    const dayEnd = currentDayjs.clone().date(i).endOf('date');
    const {
      isSpecialDay,
      isOffDay,
      hours: availabilityList,
    } = getDateAvailability(
      currentYear,
      currentMonth,
      i,
      dayOfWeek,
      businessHours,
    );
    if (i !== daysInMonth) {
      temp.push({
        id: `${currentYear}-${currentMonth}-${i > 9 ? '' : '0'}${i}`,
        isCurrentMonth: true,
        label: `${i}`,
        dayOfWeek: dayOfWeek,
        isSaturday: dayOfWeek === 6,
        isSunday: dayOfWeek === 0,
        holidayName,
        availabilityList,
        isOffDay,
        isSpecialDay,
      });
    } else {
      temp.push({
        id: `${currentYear}-${currentMonth}-${i > 9 ? '' : '0'}${i}`,
        isCurrentMonth: true,
        label: `${i}`,
        dayOfWeek: dayOfWeek,
        isSaturday: dayOfWeek === 6,
        isSunday: dayOfWeek === 0,
        holidayName,
        availabilityList,
        isOffDay,
        isSpecialDay,
      });
      temp = [...temp, ...backFiller];
      res.push(temp);
    }
  }
  return res;
};

export const getLabelClassName = (
  day: CalendarAvailabilityDay,
  isSelected: boolean,
  isToday: boolean,
) => {
  const { isSaturday, isSunday, holidayName } = day;
  if (isToday) {
    return 'color--white bgColor--c-green';
  }
  if (isSelected) {
    return 'bgColor--c-grey';
  }
  if (isSunday || holidayName) {
    return 'color--c-red';
  }
  if (isSaturday) {
    return 'color--c-blue';
  }
  return 'color--c-black';
};

export const getDayOfWeekTextSingle = (dayOfWeek?: number) => {
  switch (dayOfWeek) {
    case 6:
      return '日';
    case 5:
      return '土';
    case 4:
      return '金';
    case 3:
      return '木';
    case 2:
      return '水';
    case 1:
      return '火';
    case 0:
      return '月';
    default:
      return '';
  }
};

export const getDayOfWeekText = (dayOfWeek?: number) => {
  if (dayOfWeek === undefined) return '';
  return `${getDayOfWeekTextSingle(dayOfWeek === 0 ? 6 : dayOfWeek - 1)}曜日`;
};

export const getAvailabilityByDate = (
  date: string,
  businessHours?: BusinessHours,
): SpecialAvailability => {
  const currentDayjs = dayjs(date, 'YYYY-MM-DD');
  const dayOfWeek = currentDayjs.day();

  const { isSpecialDay, isOffDay, hours } = getDateAvailability(
    currentDayjs.format('YYYY'),
    currentDayjs.format('MM'),
    currentDayjs.date(),
    dayOfWeek,
    businessHours,
  );
  return {
    year: parseInt(currentDayjs.format('YYYY')),
    month: parseInt(currentDayjs.format('MM')),
    day: currentDayjs.date(),
    hours: isOffDay ? [] : hours,
  };
};

const removeSpecialDay = (
  specialDays: SpecialAvailability[],
  date: string,
): SpecialAvailability[] => {
  let res: SpecialAvailability[] = specialDays;
  res = res.filter((x) => {
    const xDate = `${x.year}-${x.month == null ? 'null' : Number(x.month)}-${x.day == null ? 'null' : Number(x.day)}`;
    return xDate !== date;
  });
  return res;
};

const removeSelectedDays = (
  specialDays: SpecialAvailability[],
  values: Dayjs[],
): SpecialAvailability[] => {
  let res: SpecialAvailability[] = specialDays;
  values.forEach((date) => {
    res = removeSpecialDay(res, date.format('YYYY-M-D'));
  });
  return res;
};

export const getFinalSpecialDays = (
  values: Dayjs[],
  availability: TempHourAvailability[],
  defaultDateAvailabilityDate: string,
  businessHours?: BusinessHours,
  replace?: boolean,
): SpecialAvailability[] => {
  let res: SpecialAvailability[] = businessHours?.specialDays || [];
  // if replacing, we need to delete the original date
  if (replace) {
    res = removeSpecialDay(
      res,
      dayjs(defaultDateAvailabilityDate).format('YYYY-M-D'),
    );
  }
  // remove existing special days that match the new dates
  res = removeSelectedDays(res, values);

  // add all new dates to the special days
  values.forEach((date) => {
    res.push({
      year: parseInt(date.format('YYYY')),
      month: parseInt(date.format('MM')),
      day: parseInt(date.format('DD')),
      hours: availability.filter(
        (h) =>
          h.from.hour !== null &&
          h.to.hour !== null &&
          h.from.minute !== null &&
          h.to.minute !== null,
      ),
    });
  });
  if (businessHours) {
    // remove special days that match the weekly hours as they are no longer special
    res = res.filter((x: SpecialAvailability) => {
      const { weekly } = businessHours;
      const dayOfWeek = dayjs(
        `${x.year}-${x.month == null ? 'null' : Number(x.month)}-${x.day == null ? 'null' : Number(x.day)}`,
        'YYYY-M-D',
      ).day();
      const hoursWeekly = getWeeklyAvailability(dayOfWeek, weekly);
      const s1 = JSON.stringify(hoursWeekly);
      const s2 = JSON.stringify(x.hours);
      return s1 !== s2;
    });
  }
  return res;
};
